/**
 * @module upper/request/1_0
 * @description 请求组件
 * @author sinchen <sinchen@lexinfintech.com>
 * @version 1.0
 */
import axios from 'axios'
import { Message as M_MESSAGE } from 'element-ui'

let G_AXIOS = {} // axios实例
let G_REPORT_PREFIX = 'COMMON'

// OA数据上报
const G_DATA_REPORT = (params) => {
    window && window.LexinUnifyReport && window.LexinUnifyReport.clickReport({
        event_pos: `${G_REPORT_PREFIX}.${params.event_pos}`,
        event_pos_name: params.event_pos_name,
        page_name: params.page_name,
        extend_info: params.extend_info
    })
}
// 工具方法
const G_UTIL = {
    /**
     * @function formatRequestData
     * @description 格式化请求入参1.用户网关添加data & system，2.(rc&oa)params2Str=true转{ data:'' } 3.(rc&oa)添加operator入参
     * @param {object} option 配置
     * @param {object} option.ajaxOption 请求配置，同ajax方法参数
     * @param {object} option.commonData 请求公共参数
     * @param {string} option.gatewayType 请求网关类型
     * @param {string} option.operator 请求操作人
     * @return {object}       格式化后请求入参
     */
    formatRequestData (option = {}) {
        let ajax_option = option.ajaxOption || {}
        let common_data = option.commonData || {}
        let gateway_type = option.gatewayType || ''
        let operator = option.operator || ''
        let option_data = Object.assign({}, common_data, ajax_option.data) // 接口入参，添加实例公共入参
        let request_data = {}
        switch (gateway_type) {
            case 'platform': // 用户网关，添加data & system
                request_data = {
                    data: option_data,
                    system: { controller: option_data.controller || '' }
                }
                break
            case 'fintech': // 消金网关，添加data
                request_data = {
                    data: option_data
                }
                break
            case 'origin': // origin类型不做任何处理
                request_data = ajax_option.data
                break
            case 'oa':
            case 'rc':
            default:
                // params2Str配置把参数转换为字符串传到后端,约定规范: { data:'' }
                request_data = ajax_option.params2Str ? { data: JSON.stringify(option_data) } : option_data

                // 是否需要传入operator
                if (operator) {
                    request_data.operator = operator
                }
        }

        return request_data
    },

    /**
     * @function formatFintechResponse
     * @description 格式化消金网关请求响应，处理状态码统一转换为retcode,retmsg;
     * @param  {Object} ret 请求响应
     * @return {Object}
     * @example
     * {
     *     code: 0,
     *     msg: 'ok',
     *     data: {},
     *     system:{}
     * }
     * ========>
     * {
     *     code: 1000,
     *     msg: '异常',
     *     data: {},
     *     system:{}
     * }
     */
    formatFintechResponse (ret) {
        // 业务逻辑data
        let retcode = ret.code // 状态码
        let retmsg = ret.msg || 'ok' // 状态信息
        let data = ret.data || {} // 容错处理

        ret.retcode = retcode
        ret.retmsg = retmsg
        ret.data = data

        // 删除已经转义的code和msg字段
        delete ret['code']
        delete ret['msg']

        return ret
    },

    /**
     * @function formatPlatformResponse
     * @description 格式化平台用户网关请求响应，处理状态码统一转换为retcode,retmsg; 外层为0则取内层状态码为请求状态码
     * @param  {Object} ret 请求响应
     * @return {Object}
     * @example
     * {
     *     result: 0, res_info: 'ok',
     *     data: { result: 1000, res_info: '异常', result_rows: {} }
     * }
     * ========>
     * {
     *     retcode: 1000, retmsg: '异常',  result_rows: {}
     * }
     */
    formatPlatformResponse (ret) {
        // 业务逻辑data
        let data = ret.data || {}
        let retcode = 0 // 状态码
        let retmsg = 'ok' // 状态信息

        // 内外两层都返回正常
        if (+ret.result === 0 && +data.result === 0) {
            retcode = data.result
            retmsg = data.res_info || retmsg
        } else {
            retcode = -1
            retmsg = 'error'

            // 若外层不为0 取外层返回
            if (+ret.result !== 0) {
                retcode = ret.result || retcode
                retmsg = ret.res_info || retmsg
            } else { // 取内层
                retcode = data.result
                retmsg = data.res_info || retmsg
            }
        }

        // 转换data数据格式，并将外层system参数移至内层
        data.retcode = retcode
        data.retmsg = retmsg
        data.system = ret.system

        // 以后台实际返回的system参数为准，不以app存储的为准
        // gateway侧会对返回的system参数做二次处理，每一次请求返回的内容都是从session内get到的实际用户态信息
        // 若接口异常 gateway侧会以请求传过来的system参数做降级处理，接口异常时，后台的服务不会被调用；
        data.is_login = this.checkLogin(ret.system)

        // 删除已经转义的result和res_info字段
        delete data['result']
        delete data['res_info']

        return data
    },

    /**
     * @function checkLogin
     * @description 根据system参数判断是否已经登录
     * @param  {object} system_data system接口出参的system参数
     * @return {boolean}
     */
    checkLogin (system_data) {
        return !!(system_data && system_data.session_id && system_data.token_id && system_data.uid)
    }
}

class Request {
    /**
     * @class Request
     * @description 请求构造函数
     * @param {object} params 初始化入参
     * @param {object} params.reportConfig 上报配置
     * @param {boolean} params.reportConfig.useReport 是否上报,默认开启
     * @param {string} params.reportConfig.prefix 上报前缀
     * @param {object} params.axiosConfig axios配置
     * @param {number} params.axiosConfig.timeout 超时时间
     * @param {object} params.axiosConfig.headers 请求头
     * @param {string} params.operator 操作人
     * @param {Object} params.loadingInstance loading实例 { showLoading () {}, hideLoading () {} }
     * @param {string} params.gatewayType 网关类型 支持'oa' OA网关 /'rc' 风控OA网关/platform 平台用户网关
     * @param {object} params.commonData 接口公共入参
     * @example
     * import M_REQUEST from '~request@1.0'
     *
     * const HTTP = new M_REQUEST({ gatewayType: platform, operator: 'sin' })
     */
    constructor (params = {}) {
        let axios_config = params.axiosConfig || {} // axios配置
        let report_config = params.reportConfig || {}
        let use_report = report_config.useReport || true // 是否上报，默认开启
        let report_prefix = report_config.prefix || '' // 上报前缀

        if (!report_prefix) { // 没有默认前缀，根据url二三级域名来设置一个
            if (location && location.host) {
                report_prefix = location.host.split('.').slice(0, 2).join('_').toUpperCase()
            } else {
                // 默认前缀
                report_prefix = 'COMMON'
            }
        }
        G_REPORT_PREFIX = report_prefix

        // axios实例
        G_AXIOS = axios.create({
            timeout: axios_config.timeout || 8000, // 超时时间
            headers: axios_config.headers || { // 默认头部
                'Content-Type': 'application/json; charset=utf-8',
                'X-Requested-With': 'XMLHttpRequest'
            },
            withCredentials: true,
            ...axios_config
        })

        // 其他配置(操作人)
        this.operator = params.operator
        // 网关类型
        this.gatewayType = params.gatewayType
        // 公共入参
        this.commonData = params.commonData || {}
        // loading对象
        this.loadingInstance = params.loadingInstance || {}

        // 添加请求拦截器
        G_AXIOS.interceptors.request.use(
            (config) => {
                // 请求开始时间
                config.request_start_time = Date.now()
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )

        // 添加响应拦截器
        G_AXIOS.interceptors.response.use(
            (response) => {
                // 计算请求耗时并打印
                const request_end_time = Date.now()
                const duration = request_end_time - response.config.request_start_time
                const request_data = response.config.data || {}

                // 接口数据上报
                use_report && !request_data.fe_no_need_report && G_DATA_REPORT({
                    event_pos: 'REQUEST_REPORT',
                    event_pos_name: '接口数据上报',
                    extend_info: Object.assign({url: response.config.url, response_data: response.data, request_params: request_data, duration: duration})
                })
                return response
            },
            (error) => {
                // 计算请求耗时并打印
                const request_end_time = Date.now()
                const duration = request_end_time - error.config.request_start_time
                const request_data = error.config.data || {}

                use_report && !request_data.fe_no_need_report && G_DATA_REPORT({
                    event_pos: 'ERROR_REPORT',
                    event_pos_name: '接口网络异常',
                    extend_info: Object.assign({url: error.config.url, err_msg: error && error.message, duration: duration})
                })
                return Promise.reject(error)
            }
        )
        return this
    }

    /**
     * @function _handleAjaxResponse
     * @private
     * @description ajax请求响应处理，接口异常会抛出异常
     * @param  {Object} params 配置参数
     * @param {Object} params.res 请求出参
     * @param {Function} params.callback 请求成功后处理回调，入参为格式化后的接口出参
     */
    _handleAjaxResponse (params = {}) {
        let res = params.res || {}
        let callback = params.callback || (() => {
        })
        let ret = res.data || {}

        // origin类型不做任何处理
        if (this.gatewayType === 'origin') {
            callback(ret)
            return
        }

        if (this.gatewayType === 'platform') {
            ret = G_UTIL.formatPlatformResponse(ret)
        } else if (this.gatewayType === 'fintech') {
            ret = G_UTIL.formatFintechResponse(ret)
        }

        let err = {} // 错误对象
        let retcode = +ret.retcode // 状态码

        // 接口正常，返回接口数据
        if (retcode === 0) {
            // oa网关返回result_rows第一个对象
            if (this.gatewayType === 'oa') {
                callback(ret.result_rows[0])
            } else {
                callback(ret)
            }
        } else {
            // 接口异常，组装错误信息，在catch函数内处理
            err.code = retcode
            err.message = ret.retmsg || '系统异常，请稍后再试！'
            throw err
        }
    }

    /**
     * @function _handleAjaxError
     * @private
     * @description ajax请求异常处理
     * @param  {Object} params 配置参数
     * @param {Object} params.error 异常
     * @param {Function} params.callback 异常处理回调，入参为异常参数
     */
    _handleAjaxError (params = {}) {
        let error = params.error || {}
        let callback = params.callback || (() => {
        })
        let errmsg = '' // 提示消息
        let option = params.option || {}

        // origin类型不做任何处理
        if (this.gatewayType === 'origin') {
            callback(error)
            return
        }

        // 判断是否加上错误码
        if (error.code) {
            errmsg = `[${error.code}]${error.message}`
        } else {
            errmsg = error.message
        }

        // 判断是否需要提示
        if (!option.hasOwnProperty('isShowError') || option.isShowError) {
            M_MESSAGE({
                message: errmsg || error,
                type: 'error'
            })
        }

        callback(error)
    }

    /**
     * @function ajax
     * @description 请求方法
     * @param {object} option 请求配置
     * @param {string} option.url 请求链接
     * @param {string} option.method 请求方法 默认为post
     * @param {object} option.params get请求参数
     * @param {object} option.data post请求入参
     * @param {boolean} option.params2Str 是否将请求入参转成字符串，用data传递 { data:'{...}' }
     * @param {object} option.extraConfig 请求额外配置
     * @param {boolean} option.isShowError 是否提示异常信息，默认为true
     * @return {object} 返回promise
     * @example
     * let G_REQUEST = M_REQUEST_FACTORY({operator: 'sin'})
     * G_REQUEST.ajax({
     *   url: '/xxx.json',
     *   data: {
     *      xx: 'xx'
     *   }
     * }).then((res) => {
     *
     * }).catch((err) => {
     *
     * })
     */
    ajax (option = {}) {
        return new Promise((resolve, reject) => {
            let request_data = G_UTIL.formatRequestData({
                ajaxOption: option,
                commonData: this.commonData,
                gatewayType: this.gatewayType,
                operator: this.operator
            })

            // loading 实例 { showLoading: funa, hideLoading: funb }
            let loading_instance = this.loadingInstance
            // loading配置
            let loading_opts = option.loadingOpts || {}
            // 是否需要展示loading = 配置isShowLoading && 有loading实例
            let is_need_loading = option.isShowLoading && loading_instance.showLoading && loading_instance.hideLoading

            // 展示&隐藏loading，只在需要展示loading时处理
            let _show_loading = () => is_need_loading && loading_instance.showLoading(loading_opts)
            let _hide_loading = () => is_need_loading && loading_instance.hideLoading(loading_opts)

            _show_loading()
            G_AXIOS({
                headers: option.headers || {},
                url: option.url,
                method: option.method || 'post',
                data: request_data,
                params: option.params,
                ...option.extraConfig
            }).then((res) => {
                _hide_loading()

                this._handleAjaxResponse({
                    res,
                    callback: resolve
                })
            }).catch((error) => {
                _hide_loading()

                this._handleAjaxError({
                    error,
                    option,
                    callback: reject
                })
            })
        })
    }
}

export default function (params) {
    return new Request(params)
}
